import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/** Site key pública do Cloudflare Turnstile (vazia = captcha desativado). */
export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset:  (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

/** Carrega o script da Cloudflare uma única vez por página. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src   = SCRIPT_SRC;
      s.async = true;
      s.onload  = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile indisponível')));
      s.onerror = () => { scriptPromise = null; reject(new Error('Falha ao carregar o Turnstile')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export interface TurnstileHandle {
  /** Gera um novo desafio — tokens são de uso único. */
  reset: () => void;
}

interface Props {
  /** Recebe o token válido, ou null quando expira/falha. */
  onToken:   (token: string | null) => void;
  language?: string;
}

const Turnstile = forwardRef<TurnstileHandle, Props>(({ onToken, language }, ref) => {
  const boxRef    = useRef<HTMLDivElement>(null);
  const widgetId  = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current(null);
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    },
  }), []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;

    loadTurnstile()
      .then(api => {
        if (cancelled || !boxRef.current) return;
        widgetId.current = api.render(boxRef.current, {
          sitekey:            TURNSTILE_SITE_KEY,
          theme:              'dark',
          language:           language ?? 'auto',
          callback:           (t: string) => onTokenRef.current(t),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback':   () => onTokenRef.current(null),
        });
      })
      .catch(err => console.error('[Turnstile]', err));

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [language]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={boxRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 65 }} />;
});

Turnstile.displayName = 'Turnstile';
export default Turnstile;
